<template lang="pug">
v-container.pt-8.px-8(fluid)
  //- Top title
  v-row.mb-2(no-gutters align="center")
    v-col.shrink
      v-btn.ml-n3(text icon @click="goBack" height)
        v-icon(color="neutral700" :size="24") mdi-arrow-left
    v-col.ml-3
      .text-2xl.font-semibold.neutral700--text Prescription Report
  //- Tab header
  v-row.mt-2(no-gutters)
    v-col(cols="12" md="8")
      v-tabs(v-model="reportTab" color="basic700" :height="44" :grow="isMobileScreen")
        template(v-for="tab in tabs")
          v-tab.text-sm.font-semibold.text-none.px-4.capitalize {{ tab.text }}
    v-col(cols="12" md="4" align="end")
      .d-flex.align-center
        v-spacer
        v-btn(@click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
        customize-column-menu.column-menu(v-if="selectedTabId === 'all'" type="all_prescription_report" :showTooltip="false" iconSize="21")
        customize-column-menu.column-menu(v-if="selectedTabId === 'dispense_detail'" type="dispense_detail_report" :showTooltip="false" iconSize="21")
        v-btn.button-sm.ml-4.white--text(color="neutral700" depressed :loading="isDownloading" :disabled="!canDownloadReport" @click="onDownloadReport") Download
  v-divider
  //- Filter
  all-prescription-report-filter-row.my-4(v-if="selectedTabId === 'all'" :filters.sync="filters.all" ref="filter")
  dispense-detail-filter-row.my-4(v-if="selectedTabId === 'dispense_detail'" :filters.sync="filters.dispenseDetail" ref="filter")
  //- Listing
  .data-table.pb-4(v-if="hasAllRequiredFilters")
    v-data-table(:headers="headers" :items="prescriptions" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" disable-sort @click:row="" loader-height="4")
      //- Details
      template(v-slot:item.details="{ item }")
        v-tooltip(top color="black" content-class="black tooltip-top-arrow")
          template(#activator="{ on, attrs }")
            div(v-bind="attrs" v-on="on" style="max-width: 150px;")
              .text-sm.neutral700--text.cursor-help.truncate {{ item.details }}
          div.text-xs(style="max-width: 500px;") {{ item.details }}
  //- No filters
  v-card.rounded-lg.border-dashed.pa-8.d-flex.flex-column.justify-center.align-center.no-filter-height(v-else outlined)
    v-sheet.round-full.pa-2.neutral300.d-flex.justify-center.align-center
      v-icon(size="22" color="neutral700") $yzi-file-search-02
    .text-md.mt-4.font-semibold Apply {{ requiredItemText }} filter to display data
    .text-md.neutral500--text Apply filters located at upper left corner of the table
  v-footer.white.footer(v-if="hasAllRequiredFilters && totalCount > pageSize" app padless fixed)
    .d-flex.flex-column.grow.mb-2
      v-divider
      .d-flex.justify-end.mt-2.mx-4
        yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import AllPrescriptionReportFilterRow from '@/components/clinic/reports/filters/AllPrescriptionReportFilterRow.vue'
import DispenseDetailFilterRow from '@/components/clinic/reports/filters/DispenseDetailFilterRow.vue'

export default {
  components: {
    CustomizeColumnMenu, AllPrescriptionReportFilterRow, DispenseDetailFilterRow
  },
  data() {
    return {
      reportTab: 0,
      searchText: null,
      showSearchField: false,
      filters: {
        all: [],
        dispenseDetail: [],
      },
      isFetching: false,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      prescriptions: [],
      isDownloading: false,
      tabs: [
        {
          id: 'all',
          text: 'All'
        },
        {
          id: 'dispense_detail',
          text: 'Dispense Detail'
        },
      ],
    }
  },
  watch: {
    filters: {
      handler() {
        if (this.hasAllRequiredFilters) {
          this.currentPage = 1
          this.fetchPrescriptions()
        }
      },
      deep: true
    },
    reportTab () {
      this.prescriptions = []
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany',
      allPrescriptionReportFields: 'clinic/getAllPrescriptionReportFields',
      dispenseDetailReportFields: 'clinic/getDispenseDetailReportFields'
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    headers () {
      let fields
      // All fields
      if (this.selectedTabId === 'all') {
        fields = [...this.allPrescriptionReportFields].filter(item => item.enable)
      }
      // Dispense detail fields
      if (this.selectedTabId === 'dispense_detail') {
        const runningNumber = {
          id: 'runningNumber',
          field_name: '#',
          enable: true,
          type: 'system'
        }
        fields = [runningNumber, ...this.dispenseDetailReportFields].filter(item => item.enable)
      }

      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
        if (item.field_name === 'created') item.width = 200
      })

      return [...fields]
    },
    params () {
      let params = {
        page: this.currentPage,
        page_size: this.pageSize,
        company: this.company.id,
        search: this.searchText || null
      }
      if (this.selectedTabId === 'all') {
        this.filters.all.forEach(filter => {
          params[filter.key] = filter.value
        })
      }

      if (this.selectedTabId === 'dispense_detail') {
        params.form_type = 'registration'
        params.report_type = 'dispense_detail'
        params.relations = 'medication'
        this.filters.dispenseDetail.forEach(filter => {
          params[filter.key] = filter.value
        })
        // Date range filter
        if (params.begin && params.until) {
          params.date_range = `${params.begin},${params.until}`
          delete params.begin
          delete params.until
        }
      }

      return params
    },
    hasAllRequiredFilters () {
      let requiredFilters
      let filterKeys
      // All tab
      if (this.selectedTabId === 'all') {
        requiredFilters = ['begin', 'until', 'products']
        filterKeys = this.filters.all.map(filter => filter.key)
      }
      // Dispense detail tab
      if (this.selectedTabId === 'dispense_detail') {
        requiredFilters = ['begin', 'until']
        filterKeys = this.filters.dispenseDetail.map(filter => filter.key)
      }

      return requiredFilters.every(required => filterKeys.includes(required))
    },
    canDownloadReport () {
      return this.hasAllRequiredFilters && this.prescriptions.length > 0
    },
    selectedTabId () {
      return this.tabs[this.reportTab].id
    },
    requiredItemText () {
      if (this.selectedTabId === 'all') return 'date and medication'

      return 'date'
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    goBack () {
      return this.$router.push('/admin/clinic/settings')
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged : debounce(function () {
      this.fetchPrescriptions()
    }, 750),
    async fetchPrescriptions () {
      try {
        this.isFetching = true
        let url
        if (this.selectedTabId === 'all') url = '/product_inventories'
        if (this.selectedTabId === 'dispense_detail') url = '/leadproducts/sales_report'
        const { data } = await this.$axios.get(url, { params: this.params })
        this.prescriptions = data.results.map((result, index) => this.sanitizeData(result, index))
        this.totalCount = data.count
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    calculateRunningNumber (index) {
      const offset = (this.currentPage - 1) * this.pageSize
      return offset + index + 1
    },
    sanitizeData (obj, index) {
      let newObj = {...obj}
      // Dispense detail tab
      if (this.selectedTabId === 'dispense_detail') {
        // Running number
        newObj.runningNumber = this.calculateRunningNumber(index)
        // Date time
        newObj.datetime = this.$options.filters.displayDate(obj.datetime)
        // Address
        newObj.address = obj.address ? this.getAddressInString(obj.address) : null
        // Dosage
        newObj.drugDosage = this.getDrugDosage(obj.extra_data)

        return newObj
      }
      // All tab
      // Date time
      newObj.created = this.$options.filters.convertServerDate2(obj.created)
      // Drug label name
      newObj.drugLabel = obj.product.name
      // Lead id
      newObj.leadIC = obj?.lead?.ic || null
      // Name
      if (obj.lead) {
        newObj.name = obj.lead.name
        newObj.details = obj.lead.address
      }
      if (obj.manufacturer) {
        newObj.name = obj.manufacturer.sold_by
        let details = []
        if (obj.manufacturer.name) details.push(obj.manufacturer.name)
        if (obj.manufacturer.phone) details.push(obj.manufacturer.phone)
        if (details.length > 0) {
          newObj.details = details.join(', ')
        }
      }
      // Action
      if (obj.action_display.toLowerCase() === 'sales') newObj.action_display = 'Dispense'
      // Opening
      newObj.opening = obj.balance + (obj.change * -1)
      // Dispense by
      newObj.providerName = obj.provider?.name || null

      return newObj
    },
    pageChanged () {
      this.fetchPrescriptions()
    },
    async onDownloadReport () {
      this.isDownloading = true
      // Get key first
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
        return
      }
      // Open download url
      let baseUrl
      if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      } else {
        baseUrl = 'https://export-test.yezza.io'
      }
      // let url = `${baseUrl}/product_inventories/preview_prescription_report?key=${key}&company=${this.company.id}&products=${this.params.products}&begin=${this.params.begin}&until=${this.params.until}`
      // if (this.params.members) {
      //   url = `${url}&members=${this.params.members}`
      // }
      // window.open(url, '_blank')
      // this.isDownloading = false
      // Url setup
      let url
      if (this.selectedTabId === 'all') url = `${baseUrl}/product_inventories/preview_prescription_report`
      if (this.selectedTabId === 'dispense_detail') url = `${baseUrl}/leadproducts/sales_report`

      url = new URL(url)
      // Set key
      url.searchParams.set('key', key)
      // Prepare data
      const omitKey = ['page', 'page_size', 'search']
      for (const objKey in this.params) {
        if (!omitKey.includes(objKey) && this.params[objKey]) {
          url.searchParams.set(objKey, this.params[objKey])
        }
      }
      // Export type
      if (this.selectedTabId === 'dispense_detail') url.searchParams.set('export', 'pdf')

      window.open(url, '_blank')
      this.isDownloading = false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    getDrugDosage (data) {
      if (data.dosage_value && data.dosage_unit) return `${data.dosage_value} ${data.dosage_unit}`

      return null
    }
  },
}
</script>

<style scoped>
.no-filter-height {
  height: 40dvh;
}

::v-deep table tbody tr td:nth-child(1),
::v-deep table thead tr th:nth-child(1) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 0;
  z-index: 10;
  background: white;
}

.footer,
.column-menu {
  z-index: 11;
}
</style>